/**
 * useParkRank — computes per-park rank for each attraction by current wait time.
 *
 * Rank is the position within a park when sorted by wait time ascending
 * (shortest wait = rank 1). Only operating attractions with a numeric
 * wait time are ranked; closed or unknown-wait attractions are excluded.
 *
 * Usage: pass the full (unfiltered) attractions list. The hook returns a
 * rank map keyed by attraction ID and a groups map keyed by park name.
 */

import { useMemo } from 'react';
import type { AdminAttraction } from './useAdminAttractions';

export interface ParkRankMap {
  /** Attraction ID → 1-based rank within its park (1 = shortest wait) */
  ranks: Record<string, number>;
  /** Park name → list of ranked attraction IDs (sorted by wait time ascending) */
  groups: Record<string, string[]>;
  /** Park name → list of all operating attraction IDs (regardless of wait time) */
  operatingGroups: Record<string, string[]>;
}

/**
 * Compute per-park ranks for the given attractions.
 * Rank = position by wait time ascending within park (1 = shortest).
 * Only operating attractions with a numeric wait time are ranked.
 */
function computeParkRanks(attractions: AdminAttraction[] | null): ParkRankMap {
  if (!attractions) return { ranks: {}, groups: {}, operatingGroups: {} };

  const ranks: Record<string, number> = {};
  const groups: Record<string, string[]> = {};
  const operatingGroups: Record<string, string[]> = {};

  // Build an ID → attraction lookup for rank computation
  const byId = new Map<string, AdminAttraction>();
  for (const a of attractions) {
    byId.set(a.id, a);
  }

  // Group by park
  for (const a of attractions) {
    const park = a.parkName;
    if (!groups[park]) groups[park] = [];
    groups[park].push(a.id);
  }

  // Rank within each park (by wait time ascending)
  for (const [park, ids] of Object.entries(groups)) {
    // Resolve IDs to attractions, filter to operating with numeric wait time
    const ranked: AdminAttraction[] = ids
      .map((id) => byId.get(id))
      .filter((a): a is AdminAttraction =>
        a != null
        && a.queueStatus?.toLowerCase() === 'operating'
        && typeof a.waitTimeMinutes === 'number'
      );

    // Sort by wait time ascending
    ranked.sort((a, b) => (a.waitTimeMinutes ?? 0) - (b.waitTimeMinutes ?? 0));

    // Assign ranks (1-based)
    for (let i = 0; i < ranked.length; i++) {
      ranks[ranked[i].id] = i + 1;
    }

    groups[park] = ranked.map((a) => a.id);

    // Operating group: all operating attractions in the park (even without wait time)
    const operating = ids
      .map((id) => byId.get(id))
      .filter((a): a is AdminAttraction =>
        a != null && a.queueStatus?.toLowerCase() === 'operating'
      );
    operatingGroups[park] = operating.map((a) => a.id);
  }

  return { ranks, groups, operatingGroups };
}

export function useParkRank(attractions: AdminAttraction[] | null): ParkRankMap {
  return useMemo(() => computeParkRanks(attractions), [attractions]);
}

export { computeParkRanks };
